import { Component, ElementRef, OnInit, ViewChild, signal, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router } from '@angular/router';
import { PageEvent } from '@classi/spen';

import { LayoutService } from '@components/layout/layout.service';
import { StudentService } from '@components/student/student.service';
import { ClassesFilterService } from '@components/school-grade-class-select/classes-filter.service';
import { Student } from '@models/student';

import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { pickBy, omit, mapValues, merge, isEqual, pick, isNil } from 'lodash';
import { AuthUserService } from '@services/auth-user.service';
import { checkPresentString } from '@functions/check-string';
import { SchoolGradeClassSelectComponent } from '@components/school-grade-class-select/school-grade-class-select.component';
import { environment } from '@environments/environment';
import { GoogleAnalytics4Service } from '@services/google-analytics-4.service';
import { ControlLinkService } from '@services/control-link/control-link.service';
import { TeacherNavigationService } from '@services/navigation/teacher-navigation.service';
import { SearchConditionSavingService } from '@services/search-condition-saving.service';

const IconSort = {
  none: 'fa',
  loader: 'fa fa-spinner fa-spin',
  up: 'fa fa-caret-up',
  down: 'fa fa-caret-down',
};

@Component({
  selector: 'app-students-list',
  templateUrl: './students-list.component.html',
  styleUrls: ['./students-list.component.scss'],
  providers: [ClassesFilterService],
  standalone: false,
})
export class StudentsListComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private formBuilder = inject(UntypedFormBuilder);
  private studentService = inject(StudentService);
  private classFilterService = inject(ClassesFilterService);
  private navigationService = inject(TeacherNavigationService);
  private authUserService = inject(AuthUserService);
  private analytics4Service = inject(GoogleAnalytics4Service);
  layoutService = inject(LayoutService);
  private controlLinkService = inject(ControlLinkService);
  private searchConditionSavingService = inject(SearchConditionSavingService);

  @ViewChild(SchoolGradeClassSelectComponent) formChild: SchoolGradeClassSelectComponent;

  private apiEndPointV2 = environment.apiEndpointV2;
  filterForm: UntypedFormGroup;
  isMobileFilterOpen = false;
  queryParams = {};
  students: Array<Student>;
  errMsg: string;
  sortKey = 'class_name';
  sortOrder = 'asc';
  messageFilter: string;
  messageSort: string;

  readonly metaData = signal({
    per_page: 1,
    current_page: 1,
    next_page: 0,
    prev_page: 0,
    total_pages: 1,
    total_count: 0,
    is_there_any_student: true,
  });

  sortSelections = [
    { value: 'class_name asc', text: '校種学年組番（昇順）' },
    { value: 'class_name desc', text: '校種学年組番（降順）' },
    { value: 'number_activities asc', text: '活動記録数（昇順）' },
    { value: 'number_activities desc', text: '活動記録数（降順）' },
    { value: 'last_created_activity_time asc', text: '最新の活動記録作成日時（昇順）' },
    { value: 'last_created_activity_time desc', text: '最新の活動記録作成日時（降順）' },
    { value: 'last_updated_activity_time asc', text: '最新の活動記録更新日時（昇順）' },
    { value: 'last_updated_activity_time desc', text: '最新の活動記録更新日時（降順）' },
  ];

  sortStatus = {
    class_name: IconSort.none,
    number_activities: IconSort.none,
    last_created_activity_time: IconSort.none,
    last_updated_activity_time: IconSort.none,
  };

  formLoaded = false;
  listLoaded = false;
  isSorting = false;
  isActivityRecordDownloading = false;

  private readonly allowedParamKeys = [
    'school_year',
    'grade_id',
    'class_id',
    'sort_key',
    'sort_order',
    'page',
    'keyword',
    'class_name',
    'number_activities',
    'last_created_activity_time',
    'last_updated_activity_time',
  ];
  private readonly disallowedUrlParamKeys = ['keyword'];
  readonly saveConditionKeys = ['school_year', 'grade_id', 'class_id', 'sort_key', 'sort_order'];

  @ViewChild('activityRecordsDownloadLink') activityRecordsDownloadLink: ElementRef<HTMLAnchorElement>;

  ngOnInit() {
    this.loadList(this.route.snapshot.queryParams);
  }

  private async loadList(params: Params): Promise<void> {
    await this.classFilterService.getSchoolYears();
    await this.classFilterService.getGrades();

    const url = this.router.url.split('?')[0];
    let newParams: Params;
    if (Object.keys(params).length === 0) {
      newParams = this.searchConditionSavingService.getSearchConditions(url);
      this.classFilterService.isReinitSelectData = true;
    } else {
      newParams = params;
      const saveConditions = pick(params, this.saveConditionKeys);
      if (!this.layoutService.isTabletDownView.value) {
        delete saveConditions['sort_key'];
        delete saveConditions['sort_order'];
      }
      this.searchConditionSavingService.saveSearchConditions(url, saveConditions);
    }

    this.setQueryParamsFilter(newParams);
    await this.initForm();
    this.initSortIcon();
    this.loadStudentsList(pickBy(this.queryParams));
    this.buildMessageFilter(this.queryParams).then((returnValue) => {
      this.messageFilter = returnValue.message;
    });
    this.messageSort = this.buildMessageSort();
  }

  private navigate(params: Params, paramsHandling?: QueryParamsHandling): void {
    const apiParams = pickBy(params, (value) => !isNil(value));
    this.loadList(apiParams);

    const urlParams = omit(params, this.disallowedUrlParamKeys);
    const extras: NavigationExtras = { queryParams: urlParams, relativeTo: this.route, replaceUrl: true };
    if (paramsHandling !== undefined) {
      extras.queryParamsHandling = paramsHandling;
    }
    this.router.navigate(['.'], extras);
  }

  setQueryParamsFilter(params = {}) {
    this.queryParams = pick(params, this.allowedParamKeys);
    this.queryParams['school_year'] = params['school_year'] || this.getCurrentYear();
    this.queryParams['grade_id'] = +params['grade_id'] || this.classFilterService.grades.value[0].id;
    this.queryParams['class_id'] = +params['class_id'] || null;
  }

  getCurrentYear() {
    const currentUser = this.authUserService.retrieve();
    return this.classFilterService.schoolYears.value.includes(currentUser.schoolYear)
      ? currentUser.schoolYear
      : this.classFilterService.schoolYears.value[0];
  }

  async initForm() {
    const params = this.queryParams;
    await this.classFilterService.getClasses(params);

    this.filterForm = this.formBuilder.group({
      school_year: params['school_year'],
      grade_id: params['grade_id'],
      class_id: params['class_id'],
      keyword: params['keyword'] || '',
      sort: params['sort_key'] ? `${params['sort_key']} ${params['sort_order']}` : this.sortSelections[0].value,
    });
    this.formLoaded = true;
  }

  loadStudentsList(params = {}) {
    if (params['class_id'] === 'null') {
      params = omit(params, 'class_id');
    }
    this.sortStatus = mapValues(this.sortStatus, () => IconSort.none);
    if (this.isSorting) {
      this.sortStatus[this.sortKey] = IconSort.loader;
    } else {
      this.listLoaded = false;
    }

    this.studentService.getTeacherStudentsList(merge(params, { default_filter: this.isDefaultForm })).subscribe(
      (response) => {
        this.students = response.users;
        this.metaData.set(response.pages);

        if (this.listLoaded) {
          this.isSorting = false;
        } else {
          this.listLoaded = true;
        }

        const icon = this.sortOrder === 'asc' ? 'up' : 'down';
        this.sortStatus[this.sortKey] = IconSort[icon];
      },
      (error) => {
        this.students = [];
        this.metaData.update((data) => ({ ...data, total_pages: 1, total_count: 0 }));
        this.listLoaded = true;
        this.errMsg = <any>error;
      },
    );
  }

  pageChanged({ index }: PageEvent) {
    const params = merge(this.queryParams, { page: index + 1 });
    this.navigate(params, 'merge');
    window.scrollTo(0, 0);
  }

  toggleFilter() {
    this.isMobileFilterOpen = !this.isMobileFilterOpen;
  }

  onFilter() {
    if (checkPresentString(this.filterForm.value.keyword)) {
      this.trimTextInput();
    } else {
      this.filterForm.get('keyword').setValue('');
    }
    window.scrollTo(0, 0);
    this.isMobileFilterOpen = false;
    this.buildMessageFilter(this.queryParams).then((returnValue) => {
      this.messageFilter = returnValue.message;
    });
    const params = omit(this.filterForm.value, 'sort');
    params['request_timestamp'] = Date.now();
    this.navigate(merge(params, this.buildSortParams()), 'merge');
  }

  onChangeOrder(field) {
    if (this.isSorting) {
      return;
    }

    if (this.sortKey === field) {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortKey = field;
      this.sortOrder = 'desc';
    }

    this.filterForm.get('sort').setValue(`${this.sortKey} ${this.sortOrder}`);
    this.isSorting = true;

    this.navigate(merge(this.queryParams, this.buildSortParams()), 'merge');
  }

  onResetFilter() {
    if (this.layoutService.isTabletDownView.value) {
      [this.sortKey, this.sortOrder] = this.sortSelections[0].value.split(' ');
    }

    this.filterForm.patchValue(this.defaultFilterValues());
    this.onFilter();
    this.formChild.onResetForm();
  }

  onChangeSortValue(value: string) {
    [this.sortKey, this.sortOrder] = value.split(' ');
  }

  onClickStudentItem(student, event: KeyboardEvent) {
    if (student.viewer_permission) {
      this.navigationService.registerNavigator({ screenId: 3, filterData: omit(pickBy(this.queryParams), 'page') });
      this.controlLinkService.open(`/students/teacher/${student.id}`, event);
    }
  }

  exportActivityRecord() {
    const self = this;
    this.isActivityRecordDownloading = true;
    this.buildMessageFilter(this.queryParams).then((returnValue) => {
      let params = pickBy(self.queryParams);
      if (params['class_id'] === 'null') {
        params = omit(params, 'class_id');
      }
      params['filter_text'] = returnValue.message;

      this.studentService.getExportActivityRecords(params).subscribe(
        (response) => {
          const blob = new Blob([response.file_content], { type: 'text/csv' });
          const objectUrl = URL.createObjectURL(blob);

          const anchor = this.activityRecordsDownloadLink.nativeElement;
          anchor.setAttribute('href', objectUrl);
          anchor.setAttribute('download', response.file_name);
          anchor.click();
          anchor.removeAttribute('href');
          anchor.removeAttribute('download');

          URL.revokeObjectURL(objectUrl);

          this.analytics4Service.sendEvent('活動記録', '生徒一覧画面', 'CSV出力');

          this.isActivityRecordDownloading = false;
        },
        (error) => {
          this.isActivityRecordDownloading = false;
        },
      );
    });
  }

  private trimTextInput() {
    const keyword = this.filterForm.value.keyword;
    this.filterForm.get('keyword').setValue(keyword.replace(/\s/g, ''));
  }

  buildSortParams() {
    const sortParams = { sort_key: this.sortKey, sort_order: this.sortOrder };
    if (this.queryParams['page']) {
      sortParams['page'] = null;
    }
    return sortParams;
  }

  private initSortIcon() {
    this.sortKey = this.queryParams['sort_key'] || this.sortKey;
    this.sortOrder = this.queryParams['sort_order'] || this.sortOrder;
    const icon = this.sortOrder === 'asc' ? 'up' : 'down';
    this.sortStatus[this.sortKey] = IconSort[icon];
  }

  defaultFilterValues() {
    const filterValues = {
      school_year: this.getCurrentYear(),
      grade_id: this.classFilterService.grades.value[0].id,
      class_id: null,
      keyword: '',
    };

    if (this.layoutService.isTabletDownView.value) {
      filterValues['sort'] = this.sortSelections[0].value;
    }

    return filterValues;
  }

  async buildMessageFilter(params = {}) {
    let gradeName: string;
    const textsArray = [];

    const schoolYear = this.filterForm.get('school_year').value + '年度';
    const grade = this.classFilterService.grades.value.find((item) => {
      return item.id === +params['grade_id'];
    });

    if (grade) {
      gradeName = grade.school_stage_name + grade.name;
    } else {
      gradeName = this.classFilterService.grades[0].school_stage_name + this.classFilterService.grades[0].name;
    }
    if (!!this.classFilterService.classes.value && !this.listLoaded) {
      await this.classFilterService.getClasses(params);
    }

    const _class = this.classFilterService.classes.value.find((item) => {
      return item.id === +params['class_id'];
    });
    const className = _class ? _class.classroom_name : 'すべての組';
    textsArray.push(schoolYear, gradeName, className);

    const keyword = this.filterForm.get('keyword').value;
    if (keyword && keyword.length > 0) {
      textsArray.push(keyword);
    }
    return { message: textsArray.join('/'), textsArray };
  }

  buildMessageSort() {
    const match = this.sortSelections.find((item) => {
      return item.value === `${this.sortKey} ${this.sortOrder}`;
    });
    return match && match.text;
  }

  get isDefaultForm(): boolean {
    const compareValues = this.filterForm.value;

    if (!this.layoutService.isTabletDownView.value) {
      delete compareValues['sort'];
    }

    return isEqual(compareValues, this.defaultFilterValues());
  }

  get showPagination(): boolean {
    return this.metaData().total_pages > 1;
  }

  get isEmptyList(): boolean {
    return this.metaData().total_count === 0;
  }

  get isEmptyData(): boolean {
    return !this.metaData().is_there_any_student;
  }

  get isLoadingData(): boolean {
    return this.classFilterService.isLoading || !this.listLoaded || this.isSorting;
  }

  get isClassOrKeywordSpecified(): boolean {
    return this.queryParams['class_id'] !== null || (this.queryParams['keyword'] && this.queryParams['keyword'].length > 0);
  }
}
